import React from "react"
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined"
import ReportBadgeWithTooltip from "./ReportBadgeWithTooltip.molecule"
import { getVacationRangeLabel } from "../../utils/vacation.utils"
import useThemeContext from "../../hooks/contextConsumers/useThemeContext.hook"
import { TriangleFillIcon } from "../../design/assests/images/icons/triangle-fill"

export const InfoBadge = ({ iconClassName = "pi pi-comment", ...props }) => {
  return (
    <ReportBadgeWithTooltip
      type="info"
      iconClassName={`${iconClassName} text-white`}
      {...props}
    />
  )
}

export const WarningBadge = ({ iconClassName = "pi pi-history", ...props }) => {
  return (
    <ReportBadgeWithTooltip
      type="warning"
      iconClassName={`${iconClassName}`}
      {...props}
    />
  )
}

export const ErrorBadge = ({
  iconClassName = "pi pi-exclamation-triangle",
  legendProps = {
    style: {
      color: "var(--clr-danger)",
      fontSize: "12px",
      fontWeight: "bold",
    },
  },
  ...props
}) => {
  const { theme } = useThemeContext()
  return (
    <ReportBadgeWithTooltip
      type="danger"
      iconClassName={
        theme === "light" ? `${iconClassName} text-white` : undefined
      }
      iconSvg={theme === "dark" ? <TriangleFillIcon /> : undefined}
      legendProps={legendProps}
      {...props}
    />
  )
}

export const CommentBadge = ({ comment }) => {
  return (
    <ReportBadgeWithTooltip
      type="comment"
      iconClassName="pi pi-comment text-white"
      tooltipValue={
        <>
          <span className="font-bold">Comentario: </span>
          <span>{comment}</span>
        </>
      }
    />
  )
}

export const EditedAfterFinishedBadge = ({
  tooltipValue = "El día fue editado después de haber sido finalizado",
}) => {
  return (
    <ReportBadgeWithTooltip
      type="edited"
      iconClassName="pi pi-history"
      tooltipValue={tooltipValue}
      iconProps={{
        style: {
          color: "#24292F",
        },
      }}
      position="left"
    />
  )
}

export const EmptyVacationDayBadge = ({
  vacationsDateRange,
  vacationsMotive,
}) => {
  return (
    <ReportBadgeWithTooltip
      type="empty-vacation"
      iconSvg={<BeachAccessOutlinedIcon />}
      tooltipValue={
        <div>
          <div>
            <span>Vacaciones: </span>
            {getVacationRangeLabel(
              null,
              vacationsDateRange[0],
              vacationsDateRange[1],
              {
                includeNumberOfDays: false,
                includeDateRange: true,
                dateFormat: "dd/MM/yyyy",
              }
            )}
          </div>
          <span>Motivo: {vacationsMotive}</span>
        </div>
      }
    />
  )
}

export const NonEmptyVacationDayBadge = ({ vacationsMotive }) => {
  return (
    <ReportBadgeWithTooltip
      type="non-empty-vacation"
      iconSvg={<BeachAccessOutlinedIcon />}
      tooltipValue={
        <div>
          <div className="font-bold">Vacaciones con registro de tareas </div>
          {vacationsMotive && <span>Motivo: {vacationsMotive}</span>}
        </div>
      }
    />
  )
}

export const TimeDifferenceBadge = ({
  value,
  percentage = undefined,
  tooltip = undefined,
  minTreshold = 0.5,
  maxTreshold = 1,
  referenceValue = 40,
  ...props
}) => {
  const isDanger =
    value < referenceValue - minTreshold || value > referenceValue + maxTreshold
  return (
    isDanger && (
      <ReportBadgeWithTooltip
        type="danger"
        iconClassName="pi pi-exclamation-triangle text-white"
        legend={percentage}
        legendProps={{
          style: {
            fontSize: "12px",
            fontWeight: "bold",
          },
        }}
        tooltipValue={tooltip}
        className={"ml-1 " + props.className}
        {...props}
      />
    )
  )
}

export const UnexpectedTimeBadge = ({
  value,
  expectedValue,
  tooltips = {},
  minTreshold = 0.5,
  maxTreshold = 1,
  ...props
}) => {
  const isDanger =
    value < expectedValue - minTreshold || value > expectedValue + maxTreshold
  const isLess = value < expectedValue
  const legendProps = {
    style: {
      fontSize: "12px",
      fontWeight: "bold",
    },
  }
  if (!isDanger) return null
  return (
    <ReportBadgeWithTooltip
      type="danger"
      iconClassName={`pi pi-${isLess ? "arrow-down" : "arrow-up"} text-white`}
      legendProps={legendProps}
      tooltipValue={tooltips[isLess ? "isLess" : "isMore"]}
      {...props}
    />
  )
}

const CellReportBadges = ({ rowData, date }) => {
  const currentDay = rowData?.week?.[date]
  if (!currentDay) return null
  const isDayEditedAfterFinished = Boolean(currentDay?.isDayEditedAfterFinished)
  const timeDifference = Boolean(currentDay?.sisuTime > currentDay?.billedTime)
  const isEmptyVacationDay = Boolean(
    currentDay?.isVacationDay && currentDay?.totalSisuTime === 0
  )
  const isNonEmptyVacationDay = Boolean(
    currentDay?.isVacationDay && currentDay?.totalSisuTime > 0
  )
  const isDayFinishedWithComment = Boolean(currentDay?.workDayComment)

  return (
    <span className="flex gap-1">
      {isEmptyVacationDay && (
        <EmptyVacationDayBadge
          vacationsDateRange={[
            currentDay?.vacationsStartDate,
            currentDay?.vacationsEndDate,
          ]}
          vacationsMotive={currentDay?.vacationsMotive}
        />
      )}
      {isNonEmptyVacationDay && (
        <NonEmptyVacationDayBadge
          vacationsMotive={currentDay?.workDayComment}
        />
      )}
      {isDayEditedAfterFinished && <EditedAfterFinishedBadge />}
      {isDayFinishedWithComment && !isNonEmptyVacationDay && (
        <CommentBadge comment={currentDay?.workDayComment} />
      )}
      {timeDifference && (
        <ReportBadgeWithTooltip
          type="danger"
          iconClassName="pi pi-exclamation-triangle text-white"
          lagend={timeDifference}
        />
      )}
    </span>
  )
}

export default CellReportBadges
